import { Injectable } from '@angular/core';
import { PopoverPosition, PopoverCorner, PopoverAttachment, PopoverAlignment, PopoverArrowPosition } from './tour.types';
import { LayoutService } from '../layout/layout.service';

/**
 * Result of position calculation including arrow info
 */
export interface PositionResult {
  position: PopoverPosition;
  arrow?: PopoverArrowPosition;
}

@Injectable({
  providedIn: 'root'
})
export class DemoPopoverPositionService {
  private readonly POPOVER_WIDTH = 400; // Default width in pixels
  private readonly POPOVER_STEP_HEIGHT = 340; // Fixed height for step popovers (header + content + nav)
  private readonly POPOVER_SELECTION_HEIGHT = 500; // Height for tour selection screen
  private readonly TARGET_OFFSET = 16; // Distance from target element
  private readonly ARROW_SIZE = 12; // Arrow size in pixels

  constructor(private layoutService: LayoutService) {}

  /**
   * Get dynamic edge margin based on position and menu states
   */
  private getEdgeMargin(position: 'top' | 'bottom' | 'left' | 'right'): number {
    switch (position) {
      case 'top':
        return 16;
      case 'bottom':
        return 8;
      case 'left':
        return this.layoutService.leftOpen ? 8 : 16;
      case 'right':
        return this.layoutService.rightOpen ? 8 : 16;
    }
  }

  /**
   * Calculate popover position based on target selector and attachment preference
   * Falls back to corner positioning if no target or target not found
   */
  calculatePosition(
    targetSelector?: string,
    attachment: PopoverAttachment = 'auto',
    defaultCorner: PopoverCorner = 'bottom-right',
    alignment: PopoverAlignment = 'center'
  ): PositionResult {
    // If no target selector, use corner positioning (no arrow)
    if (!targetSelector) {
      return { position: this.getCornerPosition(defaultCorner) };
    }

    // Try to find target element
    const targetElement = document.querySelector(targetSelector);
    if (!targetElement) {
      console.warn(`[DemoPopoverPosition] Target element not found: ${targetSelector}, using corner position`);
      return { position: this.getCornerPosition(defaultCorner) };
    }

    // Get target element bounds
    const targetRect = targetElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Calculate position based on attachment preference
    if (attachment === 'auto') {
      return this.calculateAutoPosition(targetRect, viewportWidth, viewportHeight, alignment);
    } else {
      return this.calculateAttachmentPosition(targetRect, attachment, viewportWidth, viewportHeight, alignment);
    }
  }

  /**
   * Get position for a specific corner of the viewport
   * Always uses top/left for smooth CSS transitions
   * Uses fixed POPOVER_STEP_HEIGHT for reliable bottom positioning
   */
  private getCornerPosition(corner: PopoverCorner): PopoverPosition {
    const position: PopoverPosition = {};
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const bottomMargin = this.getEdgeMargin('bottom');

    switch (corner) {
      case 'top-left':
        position.top = this.getEdgeMargin('top');
        position.left = this.getEdgeMargin('left');
        break;
      case 'top-right':
        position.top = this.getEdgeMargin('top');
        position.left = viewportWidth - this.POPOVER_WIDTH - this.getEdgeMargin('right');
        break;
      case 'bottom-left':
        // Use fixed step height for reliable bottom positioning
        position.top = viewportHeight - this.POPOVER_STEP_HEIGHT - bottomMargin;
        position.left = this.getEdgeMargin('left');
        break;
      case 'bottom-right':
      default:
        // Use fixed step height for reliable bottom positioning
        position.top = viewportHeight - this.POPOVER_STEP_HEIGHT - bottomMargin;
        position.left = viewportWidth - this.POPOVER_WIDTH - this.getEdgeMargin('right');
        break;
    }

    return position;
  }

  /**
   * Intelligently calculate best position to avoid going off-screen
   */
  private calculateAutoPosition(
    targetRect: DOMRect,
    viewportWidth: number,
    viewportHeight: number,
    alignment: PopoverAlignment
  ): PositionResult {
    // Calculate available space on each side
    const spaceTop = targetRect.top;
    const spaceBottom = viewportHeight - targetRect.bottom;
    const spaceLeft = targetRect.left;
    const spaceRight = viewportWidth - targetRect.right;

    // Determine best side based on available space
    const verticalPreference = spaceBottom >= spaceTop ? 'bottom' : 'top';
    const horizontalPreference = spaceRight >= spaceLeft ? 'right' : 'left';

    // Calculate scores for each side (higher is better)
    const scores = {
      top: spaceTop,
      bottom: spaceBottom,
      left: spaceLeft,
      right: spaceRight
    };

    // Find the side with most space
    const bestSide = Object.entries(scores).reduce((a, b) => a[1] > b[1] ? a : b)[0] as 'top' | 'bottom' | 'left' | 'right';

    // Try to position on the best side, fall back if not enough space
    const minSpaceRequired = 200; // Minimum space needed
    
    if (scores[bestSide] >= minSpaceRequired) {
      return this.calculateAttachmentPosition(targetRect, bestSide, viewportWidth, viewportHeight, alignment);
    }

    // If no single side has enough space, use the corner with most combined space (no arrow)
    const cornerScores = {
      'top-left': scores.top + scores.left,
      'top-right': scores.top + scores.right,
      'bottom-left': scores.bottom + scores.left,
      'bottom-right': scores.bottom + scores.right
    };

    const bestCorner = Object.entries(cornerScores).reduce((a, b) => a[1] > b[1] ? a : b)[0] as PopoverCorner;
    return { position: this.getCornerPosition(bestCorner) };
  }

  /**
   * Calculate position for a specific attachment side with alignment
   */
  private calculateAttachmentPosition(
    targetRect: DOMRect,
    attachment: 'top' | 'bottom' | 'left' | 'right',
    viewportWidth: number,
    viewportHeight: number,
    alignment: PopoverAlignment = 'center'
  ): PositionResult {
    const position: PopoverPosition = {};
    const leftMargin = this.getEdgeMargin('left');
    const rightMargin = this.getEdgeMargin('right');
    const topMargin = this.getEdgeMargin('top');
    const bottomMargin = this.getEdgeMargin('bottom');
    
    // Arrow points in opposite direction of attachment
    const arrowSide = this.getOppositeSide(attachment);
    let arrowOffset = 50; // Default center (percentage)

    switch (attachment) {
      case 'top':
        // Position above target - use fixed step height for reliable positioning
        position.top = Math.max(topMargin, targetRect.top - this.TARGET_OFFSET - this.POPOVER_STEP_HEIGHT);
        
        // Horizontal alignment
        switch (alignment) {
          case 'start':
            position.left = Math.max(leftMargin, targetRect.left);
            arrowOffset = this.calculateArrowOffset(targetRect, position.left, 'horizontal', this.POPOVER_WIDTH);
            break;
          case 'end':
            position.left = Math.max(leftMargin, Math.min(
              targetRect.right - this.POPOVER_WIDTH,
              viewportWidth - this.POPOVER_WIDTH - rightMargin
            ));
            arrowOffset = this.calculateArrowOffset(targetRect, position.left, 'horizontal', this.POPOVER_WIDTH);
            break;
          case 'center':
          default:
            position.left = Math.max(
              leftMargin,
              Math.min(
                targetRect.left + (targetRect.width / 2) - (this.POPOVER_WIDTH / 2),
                viewportWidth - this.POPOVER_WIDTH - rightMargin
              )
            );
            arrowOffset = this.calculateArrowOffset(targetRect, position.left, 'horizontal', this.POPOVER_WIDTH);
            break;
        }
        break;

      case 'bottom':
        // Position below target
        position.top = targetRect.bottom + this.TARGET_OFFSET;
        
        // Horizontal alignment
        switch (alignment) {
          case 'start':
            position.left = Math.max(leftMargin, targetRect.left);
            arrowOffset = this.calculateArrowOffset(targetRect, position.left, 'horizontal', this.POPOVER_WIDTH);
            break;
          case 'end':
            position.left = Math.max(leftMargin, Math.min(
              targetRect.right - this.POPOVER_WIDTH,
              viewportWidth - this.POPOVER_WIDTH - rightMargin
            ));
            arrowOffset = this.calculateArrowOffset(targetRect, position.left, 'horizontal', this.POPOVER_WIDTH);
            break;
          case 'center':
          default:
            position.left = Math.max(
              leftMargin,
              Math.min(
                targetRect.left + (targetRect.width / 2) - (this.POPOVER_WIDTH / 2),
                viewportWidth - this.POPOVER_WIDTH - rightMargin
              )
            );
            arrowOffset = this.calculateArrowOffset(targetRect, position.left, 'horizontal', this.POPOVER_WIDTH);
            break;
        }
        break;

      case 'left':
        // Position to left of target - calculate from left edge
        position.left = Math.max(leftMargin, targetRect.left - this.TARGET_OFFSET - this.POPOVER_WIDTH);
        
        // Vertical alignment
        switch (alignment) {
          case 'start':
            position.top = Math.max(topMargin, targetRect.top);
            arrowOffset = this.calculateArrowOffset(targetRect, position.top, 'vertical', this.POPOVER_STEP_HEIGHT);
            break;
          case 'end':
            position.top = Math.max(topMargin, Math.min(
              targetRect.bottom - this.POPOVER_STEP_HEIGHT,
              viewportHeight - this.POPOVER_STEP_HEIGHT - bottomMargin
            ));
            arrowOffset = this.calculateArrowOffset(targetRect, position.top, 'vertical', this.POPOVER_STEP_HEIGHT);
            break;
          case 'center':
          default:
            position.top = Math.max(
              topMargin,
              Math.min(
                targetRect.top + (targetRect.height / 2) - (this.POPOVER_STEP_HEIGHT / 2),
                viewportHeight - this.POPOVER_STEP_HEIGHT - bottomMargin
              )
            );
            arrowOffset = this.calculateArrowOffset(targetRect, position.top, 'vertical', this.POPOVER_STEP_HEIGHT);
            break;
        }
        break;

      case 'right':
        // Position to right of target
        position.left = targetRect.right + this.TARGET_OFFSET;
        
        // Vertical alignment
        switch (alignment) {
          case 'start':
            position.top = Math.max(topMargin, targetRect.top);
            arrowOffset = this.calculateArrowOffset(targetRect, position.top, 'vertical', this.POPOVER_STEP_HEIGHT);
            break;
          case 'end':
            position.top = Math.max(topMargin, Math.min(
              targetRect.bottom - this.POPOVER_STEP_HEIGHT,
              viewportHeight - this.POPOVER_STEP_HEIGHT - bottomMargin
            ));
            arrowOffset = this.calculateArrowOffset(targetRect, position.top, 'vertical', this.POPOVER_STEP_HEIGHT);
            break;
          case 'center':
          default:
            position.top = Math.max(
              topMargin,
              Math.min(
                targetRect.top + (targetRect.height / 2) - (this.POPOVER_STEP_HEIGHT / 2),
                viewportHeight - this.POPOVER_STEP_HEIGHT - bottomMargin
              )
            );
            arrowOffset = this.calculateArrowOffset(targetRect, position.top, 'vertical', this.POPOVER_STEP_HEIGHT);
            break;
        }
        break;
    }

    return {
      position,
      arrow: {
        side: arrowSide,
        offsetPercent: Math.max(10, Math.min(90, arrowOffset)) // Clamp between 10-90%
      }
    };
  }

  /**
   * Get the opposite side for arrow placement
   */
  private getOppositeSide(side: 'top' | 'bottom' | 'left' | 'right'): 'top' | 'bottom' | 'left' | 'right' {
    const opposites: Record<string, 'top' | 'bottom' | 'left' | 'right'> = {
      'top': 'bottom',
      'bottom': 'top',
      'left': 'right',
      'right': 'left'
    };
    return opposites[side];
  }

  /**
   * Calculate arrow offset percentage to point at target center
   */
  private calculateArrowOffset(
    targetRect: DOMRect,
    popoverStart: number,
    orientation: 'horizontal' | 'vertical',
    popoverSize: number
  ): number {
    // Calculate target center
    const targetCenter = orientation === 'horizontal'
      ? targetRect.left + targetRect.width / 2
      : targetRect.top + targetRect.height / 2;
    
    // Calculate offset from popover start to target center
    const offsetFromStart = targetCenter - popoverStart;
    
    // Convert to percentage
    const percentage = (offsetFromStart / popoverSize) * 100;
    
    return percentage;
  }

  /**
   * Ensure position doesn't go off-screen
   */
  constrainToViewport(result: PositionResult): PositionResult {
    const constrained: PopoverPosition = { ...result.position };
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const leftMargin = this.getEdgeMargin('left');
    const rightMargin = this.getEdgeMargin('right');
    const topMargin = this.getEdgeMargin('top');
    const bottomMargin = this.getEdgeMargin('bottom');

    // Constrain left/right
    if (constrained.left !== undefined) {
      constrained.left = Math.max(leftMargin, Math.min(constrained.left, viewportWidth - this.POPOVER_WIDTH - rightMargin));
    }
    if (constrained.right !== undefined) {
      constrained.right = Math.max(rightMargin, Math.min(constrained.right, viewportWidth - this.POPOVER_WIDTH - leftMargin));
    }

    // Constrain top/bottom using fixed step height
    if (constrained.top !== undefined) {
      constrained.top = Math.max(topMargin, Math.min(constrained.top, viewportHeight - this.POPOVER_STEP_HEIGHT - bottomMargin));
    }
    if (constrained.bottom !== undefined) {
      constrained.bottom = Math.max(bottomMargin, Math.min(constrained.bottom, viewportHeight - this.POPOVER_STEP_HEIGHT - topMargin));
    }

    return {
      position: constrained,
      arrow: result.arrow
    };
  }
}
